import { CustomAppApiAuthProvider } from './CustomAppApiAuthProvider';
import { IContainer, IContainerItem, IContainerItemListResponse } from '../common/ContainerSchemas';
import { BaseApiProvider } from "./BaseApiProvider";
import { HttpMethod } from '../common/Enums';
import { Constants } from '../common/Constants';
import { BlacklistedExtensions } from "../common/BlacklistedExtensions";

export class ItemsApiProvider extends BaseApiProvider {
	public readonly apiUrl: string = Constants.VITE_APP_API_URL;
	public static readonly instance: ItemsApiProvider = new ItemsApiProvider();

	// Request deduplication and caching for extensions blacklist
	private pendingBlacklistRequest: Promise<BlacklistedExtensions> | null = null;
	private cachedBlacklist: BlacklistedExtensions | null = null;

	private constructor() {
		super(CustomAppApiAuthProvider.instance);
	}

	/**
	 * Invalidates the cached blacklist. Useful for testing.
	 */
	public invalidateBlacklistCache(): void {
		this.cachedBlacklist = null;
		this.pendingBlacklistRequest = null;
	}

	private validateExtension(name: string, blacklistedExtensions: BlacklistedExtensions) {
		const extensions = name.split('.').slice(1);

		if (extensions.length > 1) {
			throw new Error(`The file must contain only one extension.`);
		}

		const extension = extensions.pop()?.toLowerCase();
		if (extension && blacklistedExtensions.includes(`.${extension}`)) {
			throw new Error(`The file extension .${extension} is not allowed.`);
		}
	}

	public async search(query: string, page: number, pageSize: number, containerId?: string): Promise<any> {
		const token = await this.authProvider.getToken();
		const request = await this.createRequestInit(token, HttpMethod.GET);

		const encodedQuery = encodeURIComponent(query);
		let url = `/items?name=${encodedQuery}&page=${page}&page_size=${pageSize}&sorting_criteria=CREATED&sorting_order=DESC`;
		if (containerId) {
			url += `&container_id=${encodeURIComponent(containerId)}`;
		}
		return await this._send(this.apiUrl, url, request);
	}

	public async list(
		containerId: string,
		page: number = 1,
		pageSize: number = parseInt(Constants.VITE_APP_CONTAINER_PAGE_SIZE,
		)
	): Promise<IContainerItemListResponse> {
		const token = await this.authProvider.getToken();
		const request = await this.createRequestInit(token, HttpMethod.GET);

		const queryParams = new URLSearchParams({ parent_id: 'root', page: page.toString(), page_size: pageSize.toString() });
		const response = await this._send(this.apiUrl, `/containers/${encodeURIComponent(containerId)}/items?${queryParams.toString()}`, request) as IContainerItemListResponse;
		return response;
	}

	public async get(
		containerId: string,
		itemId: string,
		page: number = 1,
		pageSize: number = parseInt(Constants.VITE_APP_CONTAINER_PAGE_SIZE)
	): Promise<IContainerItemListResponse | IContainerItem> {
		const token = await this.authProvider.getToken();
		const request = await this.createRequestInit(token, HttpMethod.GET);

		// Check if this is a folder query or a single item query
		// If itemId is used with parent_id query param, it's for listing folder contents
		// Otherwise, it's for getting a single item
		const url = `/containers/${encodeURIComponent(containerId)}/items`;
		// Try to get as folder first (with parent_id)
		try {
			const response = await this._send(this.apiUrl, `${url}?parent_id=${itemId}&page=${page}&page_size=${pageSize}`, request) as IContainerItemListResponse;
			return response;
		} catch {
			// If that fails, try to get as single item
			const response = await this._send(this.apiUrl, `${url}/${itemId}`, request);
			return response as IContainerItem;
		}
	}

	public async createFolder(containerId: string, parentId: string, newFolderName: string): Promise<IContainer> {
		const formData = new FormData();
		formData.append('name', newFolderName);
		formData.append('type', 'folder');
		if (parentId !== 'root') {
			formData.append('parent_id', parentId);
		}

		const token = await this.authProvider.getToken();
		const request = await this.createRequestInit(token, HttpMethod.POST, formData);
		delete (request.headers as Record<string, string>)["Content-Type"];

		return await this._send(this.apiUrl, `/containers/${encodeURIComponent(containerId)}/items`, request) as IContainer;
	}

	public async createFile(containerId: string, parentId: string, file: File, blacklistedExtensions: BlacklistedExtensions): Promise<string> {
		this.validateExtension(file.name, blacklistedExtensions);

		const formData = new FormData();
		formData.append('name', file.name);
		formData.append('type', 'file');
		if (parentId !== 'root') {
			formData.append('parent_id', parentId);
		}
		formData.append('file', file);

		const token = await this.authProvider.getToken();
		const request = await this.createRequestInit(token, HttpMethod.POST, formData);
		delete (request.headers as Record<string, string>)["Content-Type"];

		return await this._send(this.apiUrl, `/containers/${encodeURIComponent(containerId)}/items`, request) as string;
	}

	public async delete(containerId: string, itemId: string): Promise<void> {
		const token = await this.authProvider.getToken();
		const request = await this.createRequestInit(token, HttpMethod.DELETE);

		await this._send(this.apiUrl, `/containers/${encodeURIComponent(containerId)}/items/${itemId}`, request);
	}

	public async rename(containerId: string, itemId: string, newName: string): Promise<IContainer> {
		const token = await this.authProvider.getToken();
		const patchOperations = [
			{
				op: 'REPLACE',
				path: '/name',
				value: newName
			}
		];
		const request = await this.createRequestInit(token, HttpMethod.PATCH, patchOperations, 'application/json-patch+json');

		return await this._send(this.apiUrl, `/containers/${encodeURIComponent(containerId)}/items/${itemId}`, request) as IContainer;
	}

	public async download(containerId: string, fileId: string): Promise<Blob> {
		try {
			const token = await this.authProvider.getToken();
			const request = await this.createRequestInit(token, HttpMethod.GET);
			const endpoint = `/containers/${encodeURIComponent(containerId)}/items/${fileId}/download`;

			const response = await fetch(this._url(this.apiUrl, endpoint), request);

			if (!response.ok) {
				const errorMessage = `Download failed with status ${response.status}: ${response.statusText}`;
				console.error(errorMessage);
				throw new Error(errorMessage);
			}

			return await response.blob();

		} catch (error) {
			console.error("Download Error:", error);
			throw error;
		}
	}

	public async getExtensionsBlacklist(): Promise<BlacklistedExtensions> {
		// Return cached blacklist if available
		if (this.cachedBlacklist) {
			console.debug('ItemsApiProvider: Using cached blacklist');
			return this.cachedBlacklist;
		}

		// If there's already a pending request, return it (deduplication)
		if (this.pendingBlacklistRequest) {
			console.debug('ItemsApiProvider: Using pending blacklist request (deduplication)');
			return this.pendingBlacklistRequest;
		}

		// Create a new request
		console.debug('ItemsApiProvider: Fetching extensions blacklist from API');
		this.pendingBlacklistRequest = this.fetchExtensionsBlacklist();

		try {
			const blacklist = await this.pendingBlacklistRequest;
			// Cache the result
			this.cachedBlacklist = blacklist;
			return blacklist;
		} finally {
			// Clear the pending request after it completes (success or failure)
			this.pendingBlacklistRequest = null;
		}
	}

	private async fetchExtensionsBlacklist(): Promise<BlacklistedExtensions> {
		const token = await this.authProvider.getToken();
		const request = await this.createRequestInit(token, HttpMethod.GET);
		const endpoint = `/items/extensions`;

		const response = await this._send(this.apiUrl, endpoint, request) as { blacklisted_extensions: string[] };
		return response.blacklisted_extensions || [];
	}
}
